import React, { useMemo, useCallback } from 'react';
import { useRole } from '../../context/roleContext';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faEdit,
  faTrash,
  faEye,
} from '@fortawesome/free-solid-svg-icons';
import { UI_COLORS, colorHelpers } from '../../utils/colors';
import DataTable from '../common/DataTable';
import { useTranslation } from 'react-i18next';

const ITEMS_PER_PAGE = 25;

/**
 * Format currency value to Indian Rupees
 */

/**
 * Format GST rate as percentage
 * @param {number|string} gstRate
 * @returns {string}
 */
const formatGstRate = (gstRate, noGstRateLabel) => {
  if (!gstRate || isNaN(gstRate)) return noGstRateLabel;
  return `${parseFloat(gstRate)}%`;
};

const ProductCatalogueTable = ({
  products = [],
  loading = false,
  selectedProducts = [],
  onSelectProduct,
  onSelectAll,
  onEditProduct,
  onDeleteProduct,
  onViewProduct,
  viewLoading = false,
}) => {
  const { t } = useTranslation();
  const { checkDeleteAccess, checkEditAccess } = useRole();
  const hasProducts = Array.isArray(products) && products.length > 0;
  const selectedProductsSet = useMemo(() => new Set(selectedProducts), [selectedProducts]);
  const allSelected = hasProducts && selectedProducts.length === products.length;

  const hoverColor = useMemo(
    () => colorHelpers.withOpacity(UI_COLORS.BACKGROUNDS.SECTION, 0.5),
    []
  );
  const selectedColor = useMemo(
    () => colorHelpers.withOpacity(UI_COLORS.BUTTONS.PRIMARY.background, 0.1),
    []
  );
  const selectionAccent = UI_COLORS.BUTTONS.PRIMARY.background;

  const columns = useMemo(
    () => [
      {
        id: 'selection',
        label: t('productCatalogue.table.columns.select'),
        width: '60px',
        align: 'center',
        alignItems: 'center',
        justify: 'center',
        sortable: false,
        renderHeader: ({ loading: isLoading }) => (
          <input
            type="checkbox"
            aria-label={t('productCatalogue.table.aria.selectAll')}
            checked={allSelected}
            onChange={() => onSelectAll?.(!allSelected)}
            disabled={!hasProducts || isLoading}
            className="rounded focus:ring-2"
            style={{ accentColor: selectionAccent }}
          />
        ),
        renderCell: (product) => {
          const productName = product.product_name || t('productCatalogue.table.copy.unnamedProduct');
          const ariaLabel = `${t('productCatalogue.table.aria.selectProduct')} ${productName}`;

          return (
            <input
              type="checkbox"
              aria-label={ariaLabel}
              checked={selectedProductsSet.has(product.id)}
              onChange={(event) => onSelectProduct?.(product.id, event.target.checked)}
              className="rounded focus:ring-2"
              style={{ accentColor: selectionAccent }}
            />
          );
        },
        skeleton: <div className="w-4 h-4 bg-gray-300 rounded" />,
      },
      {
        id: 'product_name',
        label: t('productCatalogue.table.columns.productName'),
        width: '200px',
        sortable: true,
        sortAccessor: (product) => (product?.product_name || '').toLowerCase(),
        renderCell: (product) => (
          <div className="flex items-center space-x-3">
            <div>
              <div className="font-medium" style={{ color: UI_COLORS.TEXT.PRIMARY }}>
                {product?.product_name || t('productCatalogue.table.copy.unnamedProduct')}
              </div>
            </div>
          </div>
        ),
        skeleton: (
          <div className="flex items-center space-x-3">
            <div className="w-8 h-8 bg-gray-300 rounded-full" />
            <div className="w-32 h-4 bg-gray-300 rounded" />
          </div>
        ),
      },
      {
        id: 'description',
        label: t('productCatalogue.table.columns.description'),
        width: '250px',
        sortable: true,
        sortAccessor: (product) => (product?.description || '').toLowerCase(),
        renderCell: (product) => (
          <div className="text-sm text-gray-600 truncate max-w-md" title={product?.description}>
            {product.description || t('productCatalogue.table.copy.noDescription')}
          </div>
        ),
        skeleton: <div className="w-40 h-4 bg-gray-300 rounded" />,
      },
      {
        id: 'category_name',
        label: t('productCatalogue.table.columns.category'),
        width: '150px',
        sortable: true,
        sortAccessor: (product) => (product?.category_name || '').toLowerCase(),
        renderCell: (product) => (
          <span
            className="inline-block px-3 py-1 rounded-full text-xs font-semibold whitespace-nowrap"
            style={{
              backgroundColor: colorHelpers.withOpacity(UI_COLORS.NAVIGATION.ACTIVE, 0.2),
              color: UI_COLORS.TEXT.PRIMARY,
            }}
          >
            {product?.category_name || t('productCatalogue.table.copy.noCategory')}
          </span>
        ),
        skeleton: <div className="w-20 h-4 bg-gray-300 rounded" />,
      },
      {
        id: 'hsn_code_value',
        label: t('productCatalogue.table.columns.hsnCode'),
        width: '130px',
        sortable: true,
        sortAccessor: (product) => (product?.hsn_code_value || '').toLowerCase(),
        renderCell: (product) => (
          <span className="text-sm font-mono" style={{ color: UI_COLORS.TEXT.SECONDARY }}>
            {product?.hsn_code_value || t('productCatalogue.table.copy.noHsnCode')}
          </span>
        ),
        skeleton: <div className="w-16 h-4 bg-gray-300 rounded" />,
      },
      {
        id: 'gst_rate',
        label: t('productCatalogue.table.columns.gstRate'),
        width: '100px',
        sortable: true,
        align: 'center',
        sortAccessor: (product) => parseFloat(product?.gst_rate || 0),
        renderCell: (product) => (
          <span className="text-sm" style={{ color: UI_COLORS.TEXT.SECONDARY }}>
            {formatGstRate(product?.gst_rate, t('productCatalogue.table.copy.noGstRate'))}
          </span>
        ),
        skeleton: <div className="w-12 h-4 bg-gray-300 rounded" />,
      },
      {
        id: 'actions',
        label: t('productCatalogue.table.columns.actions'),
        width: '120px',
        align: 'center',
        alignItems: 'center',
        justify: 'center',
        sortable: false,
        renderCell: (product) => {
          const productName = product?.product_name || t('productCatalogue.table.copy.unnamedProduct');

          return (
            <div className="flex items-center justify-center space-x-2">
              <button
                onClick={() => onViewProduct?.(product.id)}
                aria-label={`${t('productCatalogue.table.aria.viewProduct')} ${productName}`}
                disabled={viewLoading}
                className="p-2 rounded-lg transition-all duration-200 hover:scale-105 disabled:opacity-50 disabled:scale-100 disabled:cursor-not-allowed"
                style={{
                  color: UI_COLORS.TEXT.PRIMARY,
                  backgroundColor: colorHelpers.withOpacity(UI_COLORS.NAVIGATION.HOVER, 0.35),
                }}
                onMouseEnter={(event) => {
                  if (!viewLoading) {
                    event.currentTarget.style.backgroundColor = colorHelpers.withOpacity(
                      UI_COLORS.NAVIGATION.HOVER,
                      0.5
                    );
                  }
                }}
                onMouseLeave={(event) => {
                  event.currentTarget.style.backgroundColor = colorHelpers.withOpacity(
                    UI_COLORS.NAVIGATION.HOVER,
                    0.35
                  );
                }}
              >
                <FontAwesomeIcon icon={faEye} className="text-sm" />
              </button>

              {checkEditAccess(product.created_at || product.date, { isMasterData: true }) && (
                <button
                  onClick={() => onEditProduct?.(product.id)}
                  aria-label={`${t('productCatalogue.table.aria.editProduct')} ${productName}`}
                  className="p-2 rounded-lg transition-all duration-200 hover:scale-105"
                  style={{
                    color: UI_COLORS.STATUS.WARNING,
                    backgroundColor: colorHelpers.withOpacity(UI_COLORS.STATUS.WARNING, 0.1),
                  }}
                  onMouseEnter={(event) => {
                    event.currentTarget.style.backgroundColor = colorHelpers.withOpacity(
                      UI_COLORS.STATUS.WARNING,
                      0.2
                    );
                  }}
                  onMouseLeave={(event) => {
                    event.currentTarget.style.backgroundColor = colorHelpers.withOpacity(
                      UI_COLORS.STATUS.WARNING,
                      0.1
                    );
                  }}
                >
                  <FontAwesomeIcon icon={faEdit} className="text-sm" />
                </button>
              )}

              {checkDeleteAccess() && (
                <button
                  onClick={() => onDeleteProduct?.(product.id)}
                  aria-label={`${t('productCatalogue.table.aria.deleteProduct')} ${productName}`}
                  className="p-2 rounded-lg transition-all duration-200 hover:scale-105"
                  style={{
                    color: UI_COLORS.STATUS.ERROR,
                    backgroundColor: colorHelpers.withOpacity(UI_COLORS.STATUS.ERROR, 0.1),
                  }}
                  onMouseEnter={(event) => {
                    event.currentTarget.style.backgroundColor = colorHelpers.withOpacity(
                      UI_COLORS.STATUS.ERROR,
                      0.2
                    );
                  }}
                  onMouseLeave={(event) => {
                    event.currentTarget.style.backgroundColor = colorHelpers.withOpacity(
                      UI_COLORS.STATUS.ERROR,
                      0.1
                    );
                  }}
                >
                  <FontAwesomeIcon icon={faTrash} className="text-sm" />
                </button>
              )}
            </div>
          );
        },
        skeleton: (
          <div className="flex items-center justify-center space-x-2">
            <div className="w-6 h-6 bg-gray-300 rounded" />
            <div className="w-6 h-6 bg-gray-300 rounded" />
            <div className="w-6 h-6 bg-gray-300 rounded" />
          </div>
        ),
      },
    ],
    [
      allSelected,
      hasProducts,
      onSelectAll,
      selectedProductsSet,
      onSelectProduct,
      onViewProduct,
      onEditProduct,
      onDeleteProduct,
      viewLoading,
      selectionAccent,
      checkDeleteAccess,
      checkEditAccess,
      t,
    ]
  );

  const getRowStyle = useCallback(
    (product, isSelected) => ({
      backgroundColor: isSelected
        ? selectedColor
        : 'transparent',
      borderLeftColor: isSelected ? selectionAccent : 'transparent',
      borderLeftWidth: isSelected ? '3px' : '0px',
    }),
    [selectedColor, selectionAccent]
  );

  const getRowHoverStyle = useCallback(() => ({
    backgroundColor: hoverColor,
  }), [hoverColor]);

  return (
    <DataTable
      data={products}
      columns={columns}
      loading={loading}
      emptyStateTitle={t('productCatalogue.table.emptyState.title')}
      emptyStateMessage={t('productCatalogue.table.emptyState.message')}
      itemsPerPage={ITEMS_PER_PAGE}
      selectedItems={selectedProducts}
      getRowStyle={getRowStyle}
      getRowHoverStyle={getRowHoverStyle}
      keyExtractor={(product) => `product-${product.id}`}
      responsive={true}
      tableClassName="min-w-full"
      containerClassName="w-full"
      enablePagination={false}
    />
  );
};

export default ProductCatalogueTable;
